import * as React from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from '@lib/useTypedTranslation';
import { Card } from '@components/card/card';
import { TabPanel } from '@components/tabs/tab-panel';
import { LinkTab } from '@components/tabs/link-tab';

import { useWorldRoutes } from '../../../routes/parts/allWorldRoutes';
import { useCurrentWorldContext } from '../../../context/currentWorld';
import { Feature } from '../../app/productsWrapper';
import { decode } from '../../../lib/base64';
import { BatteryOverviewContainer } from '../../batteryOverview';
import { BatteryCentricContainer } from '../../battery-centric';
import { BatteryDevicesContainer } from '../../battery-devices';

import './coreBatteriesHub.css';

export const CoreBatteriesHub = () => {
  const { t } = useTranslation();
  const { pathname, search } = useLocation();
  const routes = useWorldRoutes();
  const { features } = useCurrentWorldContext();

  const hasBatteries = features.includes(Feature.batteryEssentials) || features.includes(Feature.panasonicSmartBatteryMonitor);
  const hasBatteryDevices = features.includes(Feature.batteryEssentialsDevices) || features.includes(Feature.panasonicSmartBatteryMonitorDevices);

  // tab indices depend on which feature-gated tabs are rendered, since the overview tab is always first
  let nextTabIndex = 1;
  const batteriesTabIndex = hasBatteries ? nextTabIndex++ : undefined;
  const devicesTabIndex = hasBatteryDevices ? nextTabIndex++ : undefined;

  const isBatteriesActive = pathname === routes.core.batteriesList;
  const isDevicesActive = pathname === routes.core.batteriesDevices;

  const defaultTabIndex = React.useMemo(() => {
    if (isDevicesActive && devicesTabIndex !== undefined) {
      return devicesTabIndex;
    }
    if (isBatteriesActive && batteriesTabIndex !== undefined) {
      return batteriesTabIndex;
    }
    return 0;
  }, [isDevicesActive, devicesTabIndex, isBatteriesActive, batteriesTabIndex]);

  const queryParams = new URLSearchParams(search);
  const queryId = queryParams.get('id');
  const queryTs = queryParams.get('ts');

  return (
    <Card className="core-batteries-hub" noPadding>
      <TabPanel defaultTabIndex={defaultTabIndex} enableStatePreservation tabAlignment="start">
        <LinkTab id="overview" title={t('OVERVIEW')} link={routes.core.batteries}>
          <BatteryOverviewContainer />
        </LinkTab>
        {hasBatteries && (
          <LinkTab id="batteries" title={t('BATTERY_FIRST_CAPITAL_other')} link={routes.core.batteriesList}>
            <BatteryCentricContainer
              selectedId={isBatteriesActive && queryId ? decode(queryId) : undefined}
              selectedTimestamp={isBatteriesActive ? Number(queryTs) : undefined}
            />
          </LinkTab>
        )}
        {hasBatteryDevices && (
          <LinkTab id="devices" title={t('DEVICE_other')} link={routes.core.batteriesDevices}>
            <BatteryDevicesContainer
              selectedId={isDevicesActive && queryId ? decode(queryId) : undefined}
              selectedTimestamp={isDevicesActive ? Number(queryTs) : undefined}
            />
          </LinkTab>
        )}
      </TabPanel>
    </Card>
  );
};
